import {
  calculateLineItemTaxPreview,
  taxBreakdownSourceItem,
  reconciledTaxBreakdown,
  resolveLineTaxLineType,
  type AggregatedTaxBreakdownRow,
} from '@/lib/tax/tax-utils';
import {
  isMaterializedDynamicService,
  resolveEffectivePriceStrategy,
  resolveTMLineSubtotal,
} from '@/lib/sales/pricingMode';
import { canCarryComponents } from '@/lib/sales/service-components';
// ONE definition of "is this line a membership", shared with operations'
// pricing engine and deposit base. A membership is TYPED `MEMBERSHIP` or
// carries `taxCategory: 'membership'`, and this preview used to see only the
// first — so a service-shaped membership sat inside the preview's bundle tax
// base and outside the server's, and the operator was shown a split the save
// would not store. See the package module for why it lives there.
import { isMembershipLine } from '@notifi/shared-types/membership-line';
import { resolveBundleTaxLineType } from '@/lib/sales/service-bundles';
import { sectionForType } from '@/lib/sales/line-sections';
import {
  billableAmount,
  chargesRevenue,
  discountableBase,
  isLineSold,
  type PricingTreatment,
} from '@/lib/sales/line-economics';
import type { TaxLineType, TaxResolutionResult, TaxSnapshot } from '@/types/tax';

export interface EstimatePricingPreviewLine {
  _rowId: string;
  lineItemId?: string;
  type: string;
  quantity: number;
  basePrice: number;
  laborRate: number;
  costBasis: number;
  markupPercent: number;
  priceStrategy: string;
  taxable: boolean;
  /** Catalog tax classification (materials/labor/flat_rate/exempt); when present,
   *  the preview uses it instead of deriving from `type`. */
  taxLineType?: string | null;
  /** Where this line's money posts. A `membership` category makes the line a
   *  MEMBERSHIP whatever its type says — see `isMembershipLine`. Absent means
   *  the type is the only tell, which is what it was before this field
   *  reached the preview at all. */
  taxCategory?: string | null;
  discountAmount?: number;
  discountPercent?: number;
  estimatedHours?: number;
  isOptional: boolean;
  isSelected: boolean;
  /**
   * MATERIAL / EQUIPMENT only: whether the CATALOG bills this part at its own
   * price. Only the default for `pricingTreatment` — absent reads as
   * chargeable, so every line saved before the flag existed keeps pricing
   * exactly as it did.
   */
  chargeable?: boolean;
  /**
   * MATERIAL / EQUIPMENT only: how this document charges for the part.
   * `included_in_service` prices the line at zero because its money is already
   * inside the service that carries it. Absent derives from `chargeable`.
   */
  pricingTreatment?: PricingTreatment | null;
  /** Markup percentage this line uses instead of the catalog item's own. */
  markupOverride?: number | null;
  /**
   * The unit price the operator set for THIS document, overriding whatever the
   * pricebook and the strategy would produce — the persisted spelling of the
   * spec's `unitPrice` (see `unitPriceOf` in line-economics).
   *
   * EVERY document editor writes it — estimate, job and invoice alike. The
   * estimate editor used to type the price straight over `basePrice` instead,
   * which destroyed the catalog original and, with it, the denominator of the
   * member ratio below: `memberPrice 120 / basePrice 150` priced at $200/unit
   * discounted 40% here and 20% on an invoice, for the same operator action.
   *
   * Honoured here because the operations engine honours it (`resolveUnitPrice`),
   * and a line converted in from any of them would otherwise preview at a price
   * nobody is being charged.
   */
  manualPriceOverride?: number | null;
  /**
   * Frozen dynamic-service pricing inputs, present only on a SERVICE line
   * materialized from a dynamic (Time & Materials) catalog service. Its
   * presence means `basePrice` is an already-computed amount rather than
   * something to derive — see `isMaterializedDynamicService`.
   */
  dynamicPricingConfig?: { enabled?: boolean } | null;
  /** NOT-605 — item-level member pricing (from the pricebook item). memberPrice
   *  is an absolute member unit price that wins over the plan discount;
   *  memberDiscountPercent is the item's own member % (final fallback). Mirrors
   *  the backend so the form preview matches the saved estimate. */
  memberPrice?: number | null;
  memberDiscountPercent?: number | null;

  /**
   * Bundling. A service that carries parts is one sale: the customer sees one
   * amount, so it is taxed once on the whole bundle rather than per part.
   * Mirrors `resolveBundles` in operations' pricing engine — if these diverge,
   * this preview lies to the operator about tax.
   */
  lineKey?: string | null;
  parentLineKey?: string | null;
  componentDisplayMode?: string | null;
}

export interface EstimatePricingMembershipInfo {
  discountType: string | null;
  discountValue: number;
  discountTiers?: MembershipDiscountTier[];
  eligibleLineItemTypes?: string[];
  eligibleLineItemIds?: string[];
  excludedLineItemIds?: string[];
  /** Legacy alias kept for older snapshot shapes. */
  applicableItemTypes?: string[];
  waivers?: Array<{
    lineItemId?: string | null;
    waiverType?: string | null;
    value?: number | null;
    amount?: number | null;
  }>;
  /**
   * ST parity — when the customer holds several active memberships, each plan's
   * OWN config (with its own eligibility/exclusion). The preview prices each line
   * at the best discount across these, mirroring the backend orchestrator, instead
   * of a single flattened pseudo-plan that would lose per-plan eligibility. Absent
   * or single-entry ⇒ the flat top-level config is used directly.
   */
  plans?: EstimatePricingMembershipInfo[];
}

export interface MembershipDiscountTier {
  label?: string | null;
  type: string;
  value: number;
  appliesTo?: string[];
  specificItemIds?: string[];
}

export interface EstimateLinePricingPreview {
  lineSubtotal: number;
  lineTax: number;
  lineTotal: number;
  membershipDiscountAmount: number;
  documentDiscountAmount: number;
  lineAfterDiscount: number;
  taxSnapshot: TaxSnapshot | null;
}

export interface EstimatePricingPreviewResult {
  lineItemPreviews: EstimateLinePricingPreview[];
  subtotal: number;
  taxTotal: number;
  taxBreakdown: AggregatedTaxBreakdownRow[];
  discountAmount: number;
  membershipDiscountPreview: number;
  membershipEligibleRowIds: Set<string>;
  grandTotal: number;
  /**
   * NOT-604 — grandTotal minus any selected MEMBERSHIP line (its post-tax total).
   * The base the deposit preview is charged against, mirroring the backend so a
   * recurring membership never inflates the deposit shown on the form. Equals
   * grandTotal when there is no membership line.
   */
  depositEligibleTotal: number;
}

export function computeEstimatePricingPreview(args: {
  lineItems: EstimatePricingPreviewLine[];
  taxResolution: TaxResolutionResult | null;
  pricingMode: 'STANDARD' | 'TM_OVERRIDE';
  discountValue: number;
  discountType: '$' | '%';
  membershipInfo: EstimatePricingMembershipInfo | null;
}): EstimatePricingPreviewResult {
  const { lineItems, taxResolution, pricingMode, discountValue, discountType, membershipInfo } =
    args;
  // Which lines are in the sale — with a declined service taking its parts
  // down. Decided once, here, and read everywhere below; lockstep with
  // `declineParts` in the operations pricing engine.
  const sold = soldLines(lineItems);
  const isLineSelected = (item: EstimatePricingPreviewLine): boolean => sold.has(item);

  const prepared = lineItems.map((item) => {
    // `computeLineSubtotal` is already gated on `chargesRevenue`, so this is a
    // BILLABLE amount — which is the only thing a discount may come off. Asking
    // the canonical helper says so rather than leaving it to be inferred from
    // where the zero happens to come from.
    const grossLineSubtotal = discountableBase(item, {
      billableAmount: computeLineSubtotal(item, pricingMode),
    });
    const lineDiscountAmount = computeLineDiscount(item, grossLineSubtotal);
    const lineSubtotal = roundCurrency(grossLineSubtotal - lineDiscountAmount);
    const membershipDiscountAmount = computeMembershipDiscount(item, lineSubtotal, membershipInfo);
    return {
      item,
      lineSubtotal,
      grossLineSubtotal,
      membershipEligible: membershipDiscountAmount > 0,
      membershipDiscountAmount: Math.min(membershipDiscountAmount, lineSubtotal),
      documentDiscountAmount: 0,
      lineAfterDiscount: roundCurrency(lineSubtotal - membershipDiscountAmount),
    };
  });

  const docEligible = prepared.filter(
    (line) =>
      isLineSelected(line.item) && !isMembershipLine(line.item) && line.lineAfterDiscount > 0,
  );
  const docBase = roundCurrency(docEligible.reduce((sum, line) => sum + line.lineAfterDiscount, 0));
  const discountAmount = computeDocumentDiscount(docBase, discountType, discountValue);
  allocateDocumentDiscount(docEligible, discountAmount);

  // Bundles resolve AFTER discounts: the taxable base is what the customer
  // actually pays for the bundle, not its list price.
  const nets = prepared.map((line) =>
    roundCurrency(line.lineAfterDiscount - line.documentDiscountAmount),
  );
  const bundleRoles = resolvePreviewBundles(prepared, nets, sold);

  // A bundle is taxed once, on one base; its tax is then attributed back over
  // the lines that made up that base, so the per-line figures the operator sees
  // are the ones the customer's document will print. Lockstep with
  // `resolveLineTaxes` / `attributeBundleTax` in operations.
  const resolvedTaxes = new Map<number, { taxAmount: number; taxSnapshot: TaxSnapshot | null }>();
  const unitTaxes = new Map<number, { taxAmount: number; taxSnapshot: TaxSnapshot | null }>();
  prepared.forEach((line, index) => {
    const role = bundleRoles.get(index);
    if (role?.role === 'child') return;
    unitTaxes.set(
      index,
      computeTaxOnNet({
        item: line.item,
        pricingMode,
        taxResolution,
        netAmount: role?.role === 'parent' ? role.taxBase : nets[index]!,
        bundleTaxLineType: role?.role === 'parent' ? role.taxLineType : undefined,
      }),
    );
  });
  // THE DOCUMENT IS WHAT IS TAXED — lockstep with the server's
  // `calculateDocumentTax`: each code is rounded once across the sold lines.
  reconcileDocumentTax(
    [...unitTaxes.entries()]
      .filter(([index]) => isLineSelected(prepared[index]!.item))
      .map(([, tax]) => tax),
  );
  prepared.forEach((_line, index) => {
    const role = bundleRoles.get(index);
    const computed = unitTaxes.get(index);
    if (!computed) return;
    if (role?.role !== 'parent') {
      resolvedTaxes.set(index, computed);
      return;
    }
    for (const [memberIndex, memberTax] of attributeBundleTax(role, computed)) {
      resolvedTaxes.set(memberIndex, memberTax);
    }
  });

  const lineItemPreviews = prepared.map((line, index) => {
    const net = nets[index]!;
    // Anything still missing is an unselected bundle child: offered, not sold,
    // so it is in no base and takes no share of one.
    const { taxAmount, taxSnapshot } = resolvedTaxes.get(index) ?? {
      taxAmount: 0,
      taxSnapshot: null,
    };
    return {
      lineSubtotal: line.lineSubtotal,
      lineTax: taxAmount,
      lineTotal: roundCurrency(net + taxAmount),
      membershipDiscountAmount: line.membershipDiscountAmount,
      documentDiscountAmount: line.documentDiscountAmount,
      lineAfterDiscount: net,
      taxSnapshot,
    };
  });

  const selectedLines = prepared.filter((line) => isLineSelected(line.item));
  const subtotal = roundCurrency(selectedLines.reduce((sum, line) => sum + line.lineSubtotal, 0));
  const membershipDiscountPreview = roundCurrency(
    selectedLines.reduce((sum, line) => sum + line.membershipDiscountAmount, 0),
  );
  const taxTotal = roundCurrency(
    lineItemPreviews.reduce((sum, preview, index) => {
      if (!isLineSelected(lineItems[index]!)) return sum;
      return sum + preview.lineTax;
    }, 0),
  );
  const netTotal = roundCurrency(
    lineItemPreviews.reduce((sum, preview, index) => {
      if (!isLineSelected(lineItems[index]!)) return sum;
      return sum + preview.lineAfterDiscount;
    }, 0),
  );
  const membershipEligibleRowIds = new Set(
    prepared.filter((line) => line.membershipEligible).map((line) => line.item._rowId),
  );

  // Per-jurisdiction breakdown of the included lines (same selection rule as the
  // detail) — folds each line's taxSnapshot so the form matches FinancialSummaryBlock.
  const includedForTax = lineItemPreviews.filter((_, index) => isLineSelected(lineItems[index]!));
  // Only surface named rows when their displayed cents reconcile to the stored
  // line-tax total. Mixed legacy/current snapshots fall back to one Tax row.
  const taxBreakdown = reconciledTaxBreakdown(includedForTax, taxTotal);

  const grandTotal = roundCurrency(netTotal + taxTotal);
  // NOT-604: exclude any selected membership line's post-tax total from the
  // deposit base, matching the backend deposit-eligible total.
  const membershipLineTotal = lineItemPreviews.reduce((sum, preview, index) => {
    const li = lineItems[index];
    return li && isLineSelected(li) && isMembershipLine(li) ? sum + preview.lineTotal : sum;
  }, 0);

  return {
    lineItemPreviews,
    subtotal,
    taxTotal,
    taxBreakdown,
    discountAmount,
    membershipDiscountPreview,
    membershipEligibleRowIds,
    grandTotal,
    depositEligibleTotal: roundCurrency(Math.max(0, grandTotal - membershipLineTotal)),
  };
}

export function computeLineSubtotal(
  item: EstimatePricingPreviewLine,
  pricingMode: 'STANDARD' | 'TM_OVERRIDE',
): number {
  // THE DOUBLE-CHARGING INVARIANT. A part included in its service has already
  // been paid for inside that service's price, so its own line charges ZERO —
  // subtotal, discount base, tax and grand total all follow from this one
  // figure, so zeroing it here is what makes "already paid for" true everywhere
  // rather than in each summation. Its COST is untouched: costing reads the raw
  // lines (see document-costing), so margin still carries it, and so is its
  // contribution to the service, which the service line carries.
  //
  // Only parts can be recovered through something else, and only parts carry a
  // relationship markup. A service, bundle or membership IS what is being sold,
  // is never offered either control, and must not be repriced by a field that
  // reached it by accident.
  //
  // `chargesRevenue` is the canonical gate (line-economics): it already refuses
  // to unbill anything that is not a part, so the type check below guards only
  // the relationship markup.
  if (!chargesRevenue(item)) return 0;

  // The operator's own unit price for THIS document, ahead of every strategy —
  // which is exactly where the operations engine puts it (`resolveUnitPrice`),
  // and the preview has to agree with the thing that actually bills. A line
  // carrying no override is untouched, so a line priced only by the catalog
  // prices as it always has.
  //
  // "Already paid for" is not a price the operator can type over, so this sits
  // BELOW the chargeability gate — same order as the engine.
  if (item.manualPriceOverride != null && Number.isFinite(item.manualPriceOverride)) {
    return roundCurrency(item.manualPriceOverride * item.quantity);
  }

  // A part billed on its own row is an INDEPENDENT sale: it charges its own
  // price, exactly as it would standing alone in the catalog.
  //
  // `markupOverride` — the service↔part relationship's markup — is deliberately
  // NOT applied here. That markup exists to calculate the SERVICE's price; it
  // is not real markup on the material, and letting it price the part's own row
  // sold the same material at two different prices depending on which service
  // happened to carry it.

  const effectiveStrategy = resolveEffectivePriceStrategy(item, pricingMode);

  if (effectiveStrategy === 'DYNAMIC') {
    // A dynamic service was already priced when it was put on the document: its
    // amount came from labor lines, tiered rates, markup profiles and
    // adjustments that `resolveTMLineSubtotal` has no way to express. Deriving
    // it here from `laborRate x hours` would ignore all of that and — since a
    // dynamic service carries no single laborRate — preview the service at $0
    // while the operations engine bills its frozen amount. Same rule, same
    // place in the order, as `resolveUnitPrice` there.
    if (isMaterializedDynamicService(item)) {
      return roundCurrency(item.basePrice * item.quantity);
    }
    return roundCurrency(
      resolveTMLineSubtotal({
        quantity: item.quantity,
        basePrice: item.basePrice,
        laborRate: item.laborRate,
        costBasis: item.costBasis,
        markupPercent: item.markupPercent,
        estimatedHours: item.estimatedHours,
      }),
    );
  }

  const effectivePrice =
    effectiveStrategy === 'LABOR_ONLY'
      ? item.laborRate
      : effectiveStrategy === 'COST_PLUS'
        ? item.costBasis * (1 + item.markupPercent / 100)
        : item.basePrice;
  return roundCurrency(effectivePrice * item.quantity);
}

function computeLineDiscount(item: EstimatePricingPreviewLine, lineSubtotal: number): number {
  if (lineSubtotal <= 0) return 0;
  const discountPercent = Math.max(0, Number(item.discountPercent ?? 0));
  const discountAmount = Math.max(0, Number(item.discountAmount ?? 0));
  const percentDiscount = discountPercent > 0 ? lineSubtotal * (discountPercent / 100) : 0;
  return Math.min(lineSubtotal, roundCurrency(percentDiscount + discountAmount));
}

/**
 * Which preview lines are bundles, mirroring `resolveBundles` in operations.
 *
 * Indexed rather than keyed on the line objects because the caller already
 * works positionally (`prepared[i]` ↔ `nets[i]` ↔ `lineItems[i]`), and adding a
 * second identity here is how those two views drift apart.
 */
type PreviewBundleRole =
  | {
      role: 'parent';
      taxBase: number;
      taxLineType: TaxLineType;
      /**
       * Every line index whose money is inside `taxBase` — the parent first,
       * then the selected children — with the net amount each contributed.
       * Lockstep with `members` on the engine's BundleRole.
       */
      members: { index: number; share: number }[];
    }
  | { role: 'child' };

/**
 * Which lines are the parts of which, by `lineKey` / `parentLineKey`.
 *
 * The one link resolver for this module: the bundle tax base and the decline
 * cascade must agree on what hangs under what, or a part could be folded into
 * a service's tax and still be sold when the service is not. Lockstep with
 * `resolveParts` in the operations pricing engine.
 */
function resolvePreviewParents(items: EstimatePricingPreviewLine[]): Map<number, number[]> {
  // Lockstep with resolveBundles: an ambiguous key anchors nothing, or parts
  // would attach to whichever line happened to come last.
  const indexByLineKey = new Map<string, number>();
  const ambiguousKeys = new Set<string>();
  items.forEach((item, index) => {
    const key = item.lineKey;
    if (!key) return;
    if (indexByLineKey.has(key)) ambiguousKeys.add(key);
    else indexByLineKey.set(key, index);
  });

  const childrenOf = new Map<number, number[]>();
  items.forEach((item, index) => {
    const parentKey = item.parentLineKey;
    if (!parentKey) return;
    // A line cannot be its own part — that would double its own tax base.
    if (item.lineKey === parentKey) return;
    if (ambiguousKeys.has(parentKey)) return;
    const parentIndex = indexByLineKey.get(parentKey);
    // An orphan (its service was removed) stands on its own and keeps being
    // taxed — its money is still in the total.
    if (parentIndex === undefined) return;
    // ...and so does a line whose "parent" is not a service. Lockstep with
    // resolveBundles in operations: only a service carries parts, so a link
    // naming anything else must not fold a part's tax onto its treatment.
    if (!canCarryComponents(items[parentIndex]!.type)) return;
    const siblings = childrenOf.get(parentIndex) ?? [];
    siblings.push(index);
    childrenOf.set(parentIndex, siblings);
  });
  return childrenOf;
}

/**
 * The lines actually in the sale.
 *
 * A line is sold on its own toggles (`isLineSold`) — except that A DECLINED
 * SERVICE TAKES ITS PARTS WITH IT. A part is on the document because a
 * service consumes it; it has no sale of its own. Its stored `isSelected`
 * still reads true when the customer leaves the service unticked — it is not
 * itself optional, and `customerApprove` refuses to let a non-optional line be
 * deselected — so, priced line by line, a "Billed extra" part stayed in the
 * totals after its service was declined: $100 + tax of solder for an install
 * nobody bought. The cascade is DERIVED here and never written to the row.
 */
export function soldLines(items: EstimatePricingPreviewLine[]): Set<EstimatePricingPreviewLine> {
  const sold = new Set(items.filter((item) => isLineSold(item)));
  const childrenOf = resolvePreviewParents(items);
  // Until nothing more can be removed: one pass visits a middle line's entry
  // while that line is still sold when a grandchild is listed before it. See
  // `soldLines` in `sold-lines.util.ts`.
  let removedAny = true;
  while (removedAny) {
    removedAny = false;
    for (const [parentIndex, childIndexes] of childrenOf) {
      if (sold.has(items[parentIndex]!)) continue;
      for (const childIndex of childIndexes) {
        if (sold.delete(items[childIndex]!)) removedAny = true;
      }
    }
  }
  return sold;
}

function resolvePreviewBundles(
  prepared: { item: EstimatePricingPreviewLine }[],
  nets: number[],
  sold: Set<EstimatePricingPreviewLine>,
): Map<number, PreviewBundleRole> {
  const roles = new Map<number, PreviewBundleRole>();
  const isLineSelected = (item: EstimatePricingPreviewLine): boolean => sold.has(item);
  const childrenOf = resolvePreviewParents(prepared.map((line) => line.item));

  for (const [parentIndex, childIndexes] of childrenOf) {
    const parent = prepared[parentIndex]!.item;
    if (parent.componentDisplayMode !== 'ROLLED_UP') continue;
    // Lockstep with the engine: an unselected parent is not sold, so there is
    // no bundle. Collapsing anyway would zero the tax on a still-selected part
    // whose revenue is in the totals. See resolveBundles in operations.
    if (!isLineSelected(parent)) continue;

    let taxBase = nets[parentIndex] ?? 0;
    // The parent anchors the bundle, so it leads the members: whatever cannot
    // be apportioned (a base of zero) falls to it. Its own exemption is not
    // tested here for the same reason as in the engine — `calculateLineItemTaxPreview`
    // short-circuits the whole bundle on the parent's own classification.
    const members = [{ index: parentIndex, share: nets[parentIndex] ?? 0 }];
    for (const childIndex of childIndexes) {
      const child = prepared[childIndex]!.item;
      // EXEMPT WINS OVER THE BUNDLE — lockstep with `resolveBundles` in
      // operations. A part marked `taxable: false` (or classified `exempt`) is
      // not part of what is taxed, so its money is out of the base and it takes
      // no share of the result. `calculateLineItemTaxPreview` already says
      // exempt beats the bundle treatment, but a child never reaches it: its
      // tax comes from the split. So the base has to say it too, or the
      // operator sees tax on money the server does not tax.
      if (isLineSelected(child) && !owesNoTax(child)) {
        taxBase += nets[childIndex] ?? 0;
        members.push({ index: childIndex, share: nets[childIndex] ?? 0 });
      }
      roles.set(childIndex, { role: 'child' });
    }

    roles.set(parentIndex, {
      role: 'parent',
      members,
      taxBase: roundCurrency(taxBase),
      // Decided by what is IN the bundle, not by the parent alone — lockstep
      // with `resolveBundleTaxLineType` in operations. One service anywhere
      // makes the whole thing labor; a kit with no service in it is materials.
      // Reading the parent's own snapshot instead let a service marked
      // `materials` tax its parts as goods, and showed the operator a number
      // the server would not charge.
      //
      // Read off `members` — the set whose money IS the base — so a line that
      // owes no tax gets no vote in how the rest of it is taxed. Lockstep with
      // `resolveBundles`.
      taxLineType: resolveBundleTaxLineType(members.map(({ index }) => prepared[index]!.item)),
    });
  }

  return roles;
}

/**
 * Split a bundle's tax across the lines whose money made up its base, in
 * proportion to what each contributed. Lockstep with `attributeBundleTax` in
 * operations — see that method for why the split exists.
 *
 * The bundle is still ONE base taxed on ONE treatment; every split sums back to
 * the same cents, per tax code and in total. The attributed snapshots still
 * feed the signed document's aggregate tax summary and persisted line data.
 */
function attributeBundleTax(
  bundle: Extract<PreviewBundleRole, { role: 'parent' }>,
  bundleTax: { taxAmount: number; taxSnapshot: TaxSnapshot | null },
): Map<number, { taxAmount: number; taxSnapshot: TaxSnapshot | null }> {
  const attributed = new Map<number, { taxAmount: number; taxSnapshot: TaxSnapshot | null }>();
  const breakdown = bundleTax.taxSnapshot?.breakdown ?? [];
  if (!bundleTax.taxSnapshot || breakdown.length === 0) {
    // No breakdown to split (an untaxed or exempt bundle): the parent reports
    // whatever snapshot there is, and the members owe nothing.
    bundle.members.forEach((member, position) => {
      attributed.set(
        member.index,
        position === 0 ? bundleTax : { taxAmount: 0, taxSnapshot: null },
      );
    });
    return attributed;
  }

  // A discount can in principle drive a line past zero; a negative share would
  // then hand a member tax back out of another member's pocket.
  const shares = bundle.members.map((member) => Math.max(0, Math.round(member.share * 100)));
  const entriesPerMember = bundle.members.map(() => [] as TaxSnapshot['breakdown']);
  const centsPerMember = bundle.members.map(() => 0);

  // Per CODE, not on the total: a compound code sits on a different base than
  // the one under it, so splitting each row on its own is the only way the
  // members' breakdowns still add up to the bundle's, row for row.
  for (const entry of breakdown) {
    allocateByShare(Math.round(entry.amount * 100), shares).forEach((cents, position) => {
      centsPerMember[position]! += cents;
      entriesPerMember[position]!.push({ ...entry, amount: cents / 100 });
    });
  }

  bundle.members.forEach((member, position) => {
    const cents = centsPerMember[position]!;
    attributed.set(
      member.index,
      cents === 0
        ? // A member owing nothing reports nothing, rather than a column of
          // $0.00 rows against rates it is not being charged.
          { taxAmount: 0, taxSnapshot: null }
        : {
            taxAmount: cents / 100,
            taxSnapshot: { ...bundleTax.taxSnapshot!, breakdown: entriesPerMember[position]! },
          },
    );
  });
  return attributed;
}

/**
 * Split `amountCents` over `shares` in proportion, largest remainder first, so
 * the parts sum back to exactly `amountCents`.
 */
function allocateByShare(amountCents: number, shares: number[]): number[] {
  const allocated = shares.map(() => 0);
  if (allocated.length === 0) return allocated;

  const totalShare = shares.reduce((sum, share) => sum + share, 0);
  if (totalShare <= 0) {
    // Nothing to apportion against — a bundle discounted to zero still owes a
    // fixed-amount code. It falls to the first member, which is the parent.
    allocated[0] = amountCents;
    return allocated;
  }

  // Floor and distribute the remainder, which needs a non-negative magnitude to
  // be a remainder at all.
  const sign = amountCents < 0 ? -1 : 1;
  const magnitude = Math.abs(amountCents);
  const remainders = shares.map((share, index) => {
    const exact = (magnitude * share) / totalShare;
    const floored = Math.floor(exact);
    allocated[index] = floored;
    return { index, remainder: exact - floored };
  });

  let leftover = magnitude - allocated.reduce((sum, cents) => sum + cents, 0);
  remainders.sort((a, b) => b.remainder - a.remainder);
  for (const entry of remainders) {
    if (leftover <= 0) break;
    allocated[entry.index]! += 1;
    leftover -= 1;
  }

  return sign < 0 ? allocated.map((cents) => -cents) : allocated;
}

function computeTaxOnNet(args: {
  item: EstimatePricingPreviewLine;
  pricingMode: 'STANDARD' | 'TM_OVERRIDE';
  taxResolution: TaxResolutionResult | null;
  netAmount: number;
  /** Set on a bundle parent: the treatment its members decided. */
  bundleTaxLineType?: TaxLineType;
}): { taxAmount: number; taxSnapshot: TaxSnapshot | null } {
  const { item, pricingMode, taxResolution } = args;
  // Tax operates on BILLABLE amounts only: a non-chargeable part with a
  // non-zero stored price must not create taxable revenue. `computeLineSubtotal`
  // already zeroes such a line, so this changes no figure — it states the rule
  // where the tax is charged, so the next edit upstream cannot quietly break it.
  // (A bundle parent carries its children's money and is chargeable by
  // definition, so the same call is right for it.)
  //
  // TaxABILITY is left to `calculateLineItemTaxPreview` rather than gated here
  // through `taxableBase`: a non-taxable line still needs its tax SNAPSHOT
  // written, and short-circuiting would store a null where the group and rate
  // belong.
  const netAmount = billableAmount(item, { billableAmount: args.netAmount });
  // A BUNDLE PARENT is exempt from the zero short-circuit, and only it. A
  // fixed-amount code is owed whatever the base is, so a bundle discounted to
  // $0 still carries one — `calculateLineTaxV2` charges it and hands it to the
  // parent, and stopping here showed the operator $0 tax on a sale the server
  // would save with a fee on it. Scoped to the parent deliberately: dropping
  // the guard outright would put a per-line fee on every zero-net line,
  // starting with each part priced at $0 by the double-charging rule.
  const isBundleParent = args.bundleTaxLineType !== undefined;
  if (netAmount <= 0 && !(isBundleParent && taxResolution?.group)) {
    return { taxAmount: 0, taxSnapshot: null };
  }

  const group = taxResolution?.group ?? null;
  const resolvedFrom = taxResolution?.resolvedFrom ?? null;
  const quantity = item.quantity > 0 ? item.quantity : 1;

  // The price strategy no longer reaches tax at all. It used to select a
  // labor/material split for a dynamically-priced service — a ratio invented
  // from the service's own rate fields, taxing as goods money the customer's
  // document never stated as goods. One line, one classification; lockstep
  // with `calculateLineTaxV2` in operations.
  const taxItem = {
    ...item,
    basePrice: netAmount / quantity,
    priceStrategy: 'FLAT',
    taxLineType: (item.taxLineType ?? undefined) as TaxLineType | undefined,
  };

  const { taxAmount, taxSnapshot } = calculateLineItemTaxPreview(
    taxItem,
    group,
    resolvedFrom,
    args.bundleTaxLineType,
  );
  return { taxAmount: roundCurrency(taxAmount), taxSnapshot };
}

/**
 * Round each tax code ONCE across the sold units and share the cents back —
 * the client mirror of `TaxCalculationService.calculateDocumentTax`.
 *
 * Rounding line by line lets half-cents pile up: three $10.10 lines at 8.25%
 * are $0.83 each ($2.49) while 8.25% of the $30.30 billed is $2.50 — what the
 * save returns, and what an invoice-level system prints. Cents go to the
 * largest remainder, the earlier line winning a tie. Mutates the units passed.
 */
function reconcileDocumentTax(
  units: Array<{ taxAmount: number; taxSnapshot: TaxSnapshot | null }>,
): void {
  // Keyed on the tax-group ITEM that produced the row — the server rounds each
  // group item on its own, and two codes that print identically are still two
  // totals. Rows of unknown origin (none, today) fall back to their position.
  const byCode = new Map<unknown, Array<{ entry: { amount: number }; rawCents: number }>>();
  for (const unit of units) {
    (unit.taxSnapshot?.breakdown ?? []).forEach((entry, slot) => {
      const key = taxBreakdownSourceItem(entry) ?? `slot:${slot}`;
      const list = byCode.get(key) ?? [];
      list.push({ entry, rawCents: entry.amount * 100 });
      byCode.set(key, list);
    });
  }
  for (const shares of byCode.values()) {
    // Normalised first: 0.835 x 3 is 250.49999999999997 cents in floating point.
    const target = Math.round(Number(shares.reduce((sum, s) => sum + s.rawCents, 0).toFixed(6)));
    const floors = shares.map((s) => Math.floor(Number(s.rawCents.toFixed(6))));
    let left = target - floors.reduce((sum, c) => sum + c, 0);
    const byRemainder = shares
      .map((s, i) => ({ i, remainder: s.rawCents - floors[i]! }))
      .sort((a, b) => b.remainder - a.remainder || a.i - b.i);
    for (const { i } of byRemainder) {
      if (left <= 0) break;
      floors[i]! += 1;
      left -= 1;
    }
    shares.forEach((s, i) => {
      s.entry.amount = floors[i]! / 100;
    });
  }
  for (const unit of units) {
    if (!unit.taxSnapshot) continue;
    unit.taxAmount =
      unit.taxSnapshot.breakdown.reduce((sum, entry) => sum + Math.round(entry.amount * 100), 0) /
      100;
  }
}

function computeMembershipDiscount(
  item: EstimatePricingPreviewLine,
  lineSubtotal: number,
  membershipInfo: EstimatePricingMembershipInfo | null,
): number {
  if (!membershipInfo || lineSubtotal <= 0 || isMembershipLine(item)) return 0;

  // ST parity — several active memberships: price this line at the BEST discount
  // across each plan, evaluated with its OWN eligibility (never a flattened
  // pseudo-plan, which would apply one plan's rate under another's rules). Mirrors
  // the backend orchestrator; a single plan falls through to the flat config.
  const plans = membershipInfo.plans;
  if (plans && plans.length > 1) {
    return plans.reduce(
      (best, plan) => Math.max(best, computeMembershipDiscount(item, lineSubtotal, plan)),
      0,
    );
  }

  const waiverDiscount = computeMembershipWaiverDiscount(item, lineSubtotal, membershipInfo);

  // NOT-605 — an item's explicit member price wins over the plan discount. Scale
  // by the ratio (like the backend withItemMemberBasis) so it's independent of
  // quantity and price strategy: memberTotal = lineSubtotal × memberPrice/basePrice.
  if (item.memberPrice != null && item.basePrice > 0 && item.memberPrice < item.basePrice) {
    const memberTotal = roundCurrency(lineSubtotal * (item.memberPrice / item.basePrice));
    return Math.max(waiverDiscount, roundCurrency(lineSubtotal - memberTotal));
  }

  if ((membershipInfo.discountTiers ?? []).length > 0) {
    // NOT-571 — plan eligibility gates the tier discount too; without this a
    // blanket tier discounted every line regardless of the plan's eligible/
    // excluded lists. When NO tier covers this line (ineligible, or no matching
    // tier), DON'T stop at 0 — fall through to the item-level
    // memberDiscountPercent fallback below, matching the backend (which applies
    // memberDiscountPercent only when no plan/tier rule fired) and the pricebook
    // breakdown, so the estimate preview doesn't understate the price.
    if (isPlanEligibleForLine(item, membershipInfo)) {
      const tier = computeTierMembershipDiscount(item, lineSubtotal, membershipInfo);
      // A tier that MATCHES this line prices it by plan rule and terminates the
      // fallback, even when the resolved amount is 0 (e.g. SET_PRICE at base, or
      // an explicit 0% tier) — mirrors the backend's pricedByPlanRule flag. Only
      // a NO-MATCH (no tier covers this line) falls through to the item %.
      if (tier.matched) return Math.max(waiverDiscount, tier.amount);
    }
  } else {
    const planEligible = isMembershipEligible(item, membershipInfo);
    if (membershipInfo.discountValue > 0 && planEligible) {
      const discountType = String(membershipInfo.discountType ?? '').toUpperCase();
      if (discountType === 'PERCENT') {
        return Math.max(
          waiverDiscount,
          roundCurrency(lineSubtotal * (membershipInfo.discountValue / 100)),
        );
      }
      if (discountType === 'FLAT') {
        return Math.max(waiverDiscount, Math.min(membershipInfo.discountValue, lineSubtotal));
      }
    }
  }

  // NOT-605 — final fallback: the item's own member discount % (the backend also
  // considers category/global, which the preview can't see, so this covers the
  // item-level case). Applies only when no plan discount fired above.
  if ((item.memberDiscountPercent ?? 0) > 0) {
    return Math.max(
      waiverDiscount,
      roundCurrency(lineSubtotal * ((item.memberDiscountPercent ?? 0) / 100)),
    );
  }
  return waiverDiscount;
}

function computeMembershipWaiverDiscount(
  item: EstimatePricingPreviewLine,
  lineSubtotal: number,
  membershipInfo: EstimatePricingMembershipInfo,
): number {
  const type = String(item.type ?? '')
    .trim()
    .toUpperCase();
  const waiver = (membershipInfo.waivers ?? []).find((candidate) => {
    if (candidate.lineItemId) return candidate.lineItemId === item.lineItemId;
    // Compatibility for historical waivers created before exact fee targeting.
    return type === 'FEE';
  });
  if (!waiver) return 0;

  const waiverType = String(waiver.waiverType ?? '')
    .trim()
    .toUpperCase();
  if (waiverType === 'FULL_WAIVE' || waiverType === 'DISCOUNT_TO_ZERO') {
    return lineSubtotal;
  }
  if (waiverType === 'FIXED_CREDIT') {
    const credit = Number(waiver.value ?? waiver.amount ?? 0);
    return Math.min(lineSubtotal, Math.max(0, roundCurrency(credit)));
  }
  return 0;
}

function computeTierMembershipDiscount(
  item: EstimatePricingPreviewLine,
  lineSubtotal: number,
  membershipInfo: EstimatePricingMembershipInfo,
): { matched: boolean; amount: number } {
  const tiers = membershipInfo.discountTiers ?? [];
  for (const tier of tiers) {
    if (!tierAppliesToLine(tier, item)) continue;
    // First tier that covers this line owns the pricing — matched regardless of
    // the resolved amount, so the caller stops here instead of falling through.
    const tierType = String(tier.type ?? '').toUpperCase();
    const value = Number(tier.value ?? 0);
    if (!Number.isFinite(value) || value <= 0) return { matched: true, amount: 0 };
    if (tierType === 'PERCENT') {
      return { matched: true, amount: roundCurrency(lineSubtotal * (value / 100)) };
    }
    if (tierType === 'FLAT') {
      return { matched: true, amount: Math.min(value, lineSubtotal) };
    }
    if (tierType === 'SET_PRICE') {
      return { matched: true, amount: Math.max(0, roundCurrency(lineSubtotal - value)) };
    }
    return { matched: true, amount: 0 };
  }
  return { matched: false, amount: 0 };
}

function tierAppliesToLine(
  tier: MembershipDiscountTier,
  item: EstimatePricingPreviewLine,
): boolean {
  const specificItemIds = tier.specificItemIds ?? [];
  if (specificItemIds.length > 0) {
    return Boolean(item.lineItemId && specificItemIds.includes(item.lineItemId));
  }

  const appliesTo = tier.appliesTo ?? [];
  if (appliesTo.length === 0) return true;
  const itemType = normalizeLineType(item.type);
  return appliesTo.some((type) => normalizeLineType(type) === itemType);
}

function computeDocumentDiscount(
  docBase: number,
  discountType: '$' | '%',
  discountValue: number,
): number {
  if (docBase <= 0 || discountValue <= 0) return 0;
  // Clamp both forms to the base so an over-100% / over-subtotal discount can
  // never exceed it (which would produce a negative total). (NOT-242)
  return discountType === '%'
    ? Math.min(roundCurrency(docBase * (discountValue / 100)), docBase)
    : Math.min(discountValue, docBase);
}

function allocateDocumentDiscount(
  lines: Array<{ lineAfterDiscount: number; documentDiscountAmount: number }>,
  discountAmount: number,
): void {
  if (discountAmount <= 0 || lines.length === 0) return;
  const baseCents = lines.reduce((sum, line) => sum + toCents(line.lineAfterDiscount), 0);
  const discountCents = toCents(discountAmount);
  let allocatedCents = 0;

  lines.forEach((line, index) => {
    const lineCents = toCents(line.lineAfterDiscount);
    const remainingCents = discountCents - allocatedCents;
    const isLast = index === lines.length - 1;
    const shareCents = isLast
      ? Math.min(remainingCents, lineCents)
      : Math.min(Math.round((discountCents * lineCents) / baseCents), lineCents, remainingCents);
    line.documentDiscountAmount = shareCents / 100;
    allocatedCents += shareCents;
  });
}

/**
 * NOT-571 — plan-level eligibility (excluded → eligibleIds → eligibleTypes →
 * blanket), independent of the discount value. Tiered plans force
 * `discountValue`/`discountPercent` to 0, so the tier path must gate on this
 * rather than on `isMembershipEligible` (which short-circuits on `discountValue`).
 */
function isPlanEligibleForLine(
  item: EstimatePricingPreviewLine,
  membershipInfo: EstimatePricingMembershipInfo,
): boolean {
  if (isMembershipLine(item)) return false;

  const lineItemId = item.lineItemId;
  const excludedLineItemIds = membershipInfo.excludedLineItemIds ?? [];
  if (lineItemId && excludedLineItemIds.includes(lineItemId)) return false;

  const eligibleLineItemIds = membershipInfo.eligibleLineItemIds ?? [];
  if (eligibleLineItemIds.length > 0) {
    return Boolean(lineItemId && eligibleLineItemIds.includes(lineItemId));
  }

  const eligibleLineItemTypes =
    membershipInfo.eligibleLineItemTypes ?? membershipInfo.applicableItemTypes ?? [];
  if (eligibleLineItemTypes.length > 0) {
    const itemType = normalizeLineType(item.type);
    return eligibleLineItemTypes.some((type) => normalizeLineType(type) === itemType);
  }

  return true;
}

function isMembershipEligible(
  item: EstimatePricingPreviewLine,
  membershipInfo: EstimatePricingMembershipInfo | null,
): boolean {
  if (!membershipInfo || membershipInfo.discountValue <= 0) return false;
  return isPlanEligibleForLine(item, membershipInfo);
}

/**
 * True when this line owes no tax of its own, whatever it is sold beside.
 *
 * The frontend half of `owesNoTax` in `pricing-engine.service.ts`, and it has
 * to answer identically: it decides which lines are in a bundle's taxable base,
 * and a base the two apps disagree about is a per-line split the operator sees
 * and the server refuses to save. The membership half is now literally the same
 * function on both sides (`@notifi/shared-types/membership-line`); the exempt
 * half is `resolveLineTaxLineType`, which each app already mirrors and
 * `line-tax-classification` pins.
 */
function owesNoTax(item: EstimatePricingPreviewLine): boolean {
  return (
    item.taxable === false || resolveLineTaxLineType(item) === 'exempt' || isMembershipLine(item)
  );
}

function normalizeLineType(type: string): string {
  return String(type ?? '')
    .trim()
    .toUpperCase();
}

function roundCurrency(value: number): number {
  return Math.round(value * 100) / 100;
}

function toCents(value: number): number {
  return Math.round(value * 100);
}
